import React from 'react';
import { alpha, Box, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import {
  Badge as BadgeIcon,
  Notes as NotesIcon,
  PaymentsOutlined as PaymentsOutlinedIcon,
  PermIdentity as PermIdentityIcon,
  Wc as WcIcon,
} from '@mui/icons-material';
import * as S from '../../sales/orders/ExchangeModal/ExchangeDrawer.styles';
import type { ClientDrawerFormState } from './clientCreateDrawer.types';
import {
  getGenderDisplayLabel,
  getLoyaltyTierDisplayLabel,
  getPaymentMethodDisplayLabel,
} from './clientCreateDrawer.labels';
import {
  drawerHeroBannerSx,
  drawerSectionCardSx,
  drawerSectionTitleSx,
} from './clientCreateDrawer.styles';
import ClientCreateDrawerSalesHistory from './ClientCreateDrawerSalesHistory';

export type ClientCreateDrawerReviewStepProps = {
  form: ClientDrawerFormState;
  isViewMode: boolean;
  dateOfBirthDisplay: string;
  salesHistory: {
    show: boolean;
    historyItems: React.ComponentProps<typeof ClientCreateDrawerSalesHistory>['historyItems'];
    historyLoading: boolean;
    historyError: string | null;
    historyPage: number;
    historyTotal: number;
    historyTotalPages: number;
    onRetry: () => void;
    onGoToOrder: (orderId: number) => void;
    onPrevPage: () => void;
    onNextPage: () => void;
  };
};

const ClientCreateDrawerReviewStep: React.FC<ClientCreateDrawerReviewStepProps> = ({
  form,
  isViewMode,
  dateOfBirthDisplay,
  salesHistory,
}) => {
  const genderLabel = getGenderDisplayLabel(form.gender);
  const paymentMethodLabel = getPaymentMethodDisplayLabel(form.preferredPaymentMethod);
  const tierLabel = getLoyaltyTierDisplayLabel(form.loyaltyTier);

  const marketingChip = (
    <Chip
      label={form.marketingConsent ? 'Marketing: Sí' : 'Marketing: No'}
      color={form.marketingConsent ? 'success' : 'default'}
      variant={form.marketingConsent ? 'filled' : 'outlined'}
      size="small"
      sx={(theme) => ({
        fontWeight: 700,
        ...(!form.marketingConsent && {
          borderColor: theme.palette.divider,
          color: theme.palette.text.secondary,
          backgroundColor:
            theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : 'transparent',
        }),
      })}
    />
  );

  return (
    <Box sx={{ maxWidth: 780, mx: 'auto', width: '100%' }}>
      {!isViewMode ? (
        <Box sx={(theme) => drawerHeroBannerSx(theme)}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              Revisa y confirma el registro
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Verifica que los datos estén correctos antes de crear el cliente.
            </Typography>
          </Box>
          {marketingChip}
        </Box>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <S.SectionBox sx={(theme) => drawerSectionCardSx(theme, { height: '100%' })}>
            <S.SectionHeader>
              <BadgeIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
                Contacto
              </Typography>
            </S.SectionHeader>
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Nombre
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {form.customerName !== '' ? form.customerName : '-'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CI
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <PermIdentityIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                    {form.identityDocumentNumber?.trim() !== ''
                      ? form.identityDocumentNumber.trim()
                      : '-'}
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Teléfono
                  </Typography>
                  <Typography variant="body2">
                    {form.customerPhone !== '' ? form.customerPhone : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Correo
                  </Typography>
                  <Typography variant="body2">
                    {form.customerEmail !== '' ? form.customerEmail : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Dirección
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {form.customerAddress !== '' ? form.customerAddress : '-'}
                  </Typography>
                </Box>
                {form.referenceNumber1.trim() !== '' || form.referenceNumber2.trim() !== '' ? (
                  <>
                    <Divider />
                    {form.referenceNumber1.trim() !== '' ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Nº de referencia
                        </Typography>
                        <Typography variant="body2">{form.referenceNumber1.trim()}</Typography>
                      </Box>
                    ) : null}
                    {form.referenceNumber2.trim() !== '' ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Segunda referencia
                        </Typography>
                        <Typography variant="body2">{form.referenceNumber2.trim()}</Typography>
                      </Box>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Box>
          </S.SectionBox>
        </Grid>

        <Grid item xs={12} md={6}>
          <S.SectionBox sx={(theme) => drawerSectionCardSx(theme, { height: '100%' })}>
            <S.SectionHeader>
              <PaymentsOutlinedIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
                Perfil y preferencias
              </Typography>
            </S.SectionHeader>
            <Box sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                {isViewMode ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Consentimiento de marketing
                    </Typography>
                    <Box sx={{ mt: 0.75 }}>{marketingChip}</Box>
                  </Box>
                ) : null}
                {isViewMode ? <Divider /> : null}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Fecha de nacimiento
                  </Typography>
                  <Typography variant="body2">{dateOfBirthDisplay}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Género
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                  >
                    <WcIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                    {genderLabel}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Nivel de fidelidad
                  </Typography>
                  <Typography variant="body2">{tierLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pago preferido
                  </Typography>
                  <Typography variant="body2">{paymentMethodLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Origen / referido
                  </Typography>
                  <Typography variant="body2">
                    {form.referralSource !== '' ? form.referralSource : '-'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </S.SectionBox>
        </Grid>

        <Grid item xs={12}>
          <S.SectionBox sx={(theme) => drawerSectionCardSx(theme)}>
            <S.SectionHeader>
              <NotesIcon sx={{ fontSize: 20, color: '#4f46e5' }} />
              <Typography variant="subtitle1" fontWeight={700} sx={drawerSectionTitleSx}>
                Notas
              </Typography>
            </S.SectionHeader>
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {form.customerNotes !== '' ? form.customerNotes : 'Sin notas registradas.'}
              </Typography>
            </Box>
          </S.SectionBox>
        </Grid>

        {salesHistory.show && (
          <Grid item xs={12}>
            <ClientCreateDrawerSalesHistory
              historyItems={salesHistory.historyItems}
              historyLoading={salesHistory.historyLoading}
              historyError={salesHistory.historyError}
              historyPage={salesHistory.historyPage}
              historyTotal={salesHistory.historyTotal}
              historyTotalPages={salesHistory.historyTotalPages}
              onRetry={salesHistory.onRetry}
              onGoToOrder={salesHistory.onGoToOrder}
              onPrevPage={salesHistory.onPrevPage}
              onNextPage={salesHistory.onNextPage}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ClientCreateDrawerReviewStep;
